import type {
  CLSMetricWithAttribution,
  INPMetricWithAttribution,
  LCPMetricWithAttribution,
  TTFBMetricWithAttribution,
} from "web-vitals/attribution";
import { store } from "@/redux/store";
import { isCurrentLocalDevHost } from "@/utils/is-local-dev-host";
import { getApiBaseUrl } from "./rtk-query-config";

/**
 * Core Web Vitals de campo (LCP, INP, CLS, TTFB) de quem está logado, por rota e
 * organização, em `web_vitals_amostra` (POST /metricas/web-vitals). O PageSpeed e o
 * Unlighthouse só enxergam as páginas públicas; isto mede a área logada de verdade,
 * mais as públicas de `ROTAS_PUBLICAS_MEDIDAS`, abertas sem login.
 * Só em produção e numa fração dos carregamentos (`VITE_WEB_VITALS_SAMPLE_RATE`).
 * As métricas vão juntas num beacon quando a aba fica oculta.
 *
 * Em DEV, `?webVitalsDebug=1` coleta sempre e só imprime o lote no console.
 */

type Metrica =
  | LCPMetricWithAttribution
  | INPMetricWithAttribution
  | CLSMetricWithAttribution
  | TTFBMetricWithAttribution;

type AmostraWebVital = {
  id: string;
  nome: Metrica["name"];
  valor: number;
  rating: Metrica["rating"];
  rota: string;
  navegacao: Metrica["navigationType"];
  elemento?: string;
  /** Módulo ativo do menu (eventos, ensino, pequenos-grupos...) quando há login. */
  modulo?: string;
  /** CLS: largestShiftTime (ms). */
  shiftTime?: number;
  /** INP: fases da interação (ms). */
  inputDelay?: number;
  processingDuration?: number;
  presentationDelay?: number;
};

const ms = (v: number | undefined) => (typeof v === "number" && Number.isFinite(v) ? Math.round(v * 10) / 10 : undefined);

function temposAtribuicao(metrica: Metrica): Partial<AmostraWebVital> {
  if (metrica.name === "CLS") {
    const shiftTime = ms(metrica.attribution.largestShiftTime);
    return shiftTime !== undefined ? { shiftTime } : {};
  }
  if (metrica.name === "INP") {
    const a = metrica.attribution;
    return { inputDelay: ms(a.inputDelay), processingDuration: ms(a.processingDuration), presentationDelay: ms(a.presentationDelay) };
  }
  return {};
}

const TAXA_AMOSTRAGEM_PADRAO = 0.1;
/** Mesmo teto do backend por lote. */
const MAX_METRICAS_POR_ENVIO = 10;
/** Públicas medidas também sem login (download do app e Bíblia do site da igreja). */
const ROTAS_PUBLICAS_MEDIDAS = ["/app", "/biblia"];

function ehRotaPublicaMedida(rota: string): boolean {
  return ROTAS_PUBLICAS_MEDIDAS.some((base) => rota === base || rota.startsWith(`${base}/`));
}

/** Fração dos carregamentos medidos (0 a 1). Vazio ou inválido = 10%. */
export function taxaAmostragem(bruto: string | undefined): number {
  const taxa = bruto?.trim() ? Number(bruto) : Number.NaN;
  return Number.isFinite(taxa) && taxa >= 0 && taxa <= 1 ? taxa : TAXA_AMOSTRAGEM_PADRAO;
}

/** Troca ids do path por `:id`, para agrupar por rota (`/sistema/eventos/:id`). */
export function normalizarRota(pathname: string): string {
  const segmentos = pathname
    .split("/")
    .filter(Boolean)
    .map((segmento) => {
      let s = segmento;
      try {
        s = decodeURIComponent(segmento);
      } catch {
        // mantém o segmento original
      }
      if (/^\d+$/.test(s)) return ":id";
      if (s.includes("@")) return ":id";
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) return ":id";
      if (/^[0-9a-z_-]{20,}$/i.test(s) && /\d/.test(s)) return ":id";
      return s;
    });
  return `/${segmentos.join("/")}`;
}

/** Mesmo valor do seletor de módulo do /sistema; só faz sentido com login. */
function moduloAtivo(): string | undefined {
  const estado = store.getState();
  if (!estado.authReducer?.id) return undefined;
  const modo = estado.eventoMode?.eventoMode;
  return typeof modo === "string" && modo ? modo : undefined;
}

function elementoResponsavel(metrica: Metrica): string | undefined {
  switch (metrica.name) {
    case "LCP":
      return metrica.attribution.target;
    case "INP":
      return metrica.attribution.interactionTarget;
    case "CLS":
      return metrica.attribution.largestShiftTarget;
    default:
      return undefined;
  }
}

function enviar(amostras: AmostraWebVital[], debug: boolean): void {
  const loginId = store.getState().generalReducer?.LoginId;
  const temLogin = loginId != null && loginId > 0;
  const url = `${getApiBaseUrl()}/metricas/web-vitals${temLogin ? `?loginId=${loginId}` : ""}`;

  for (let i = 0; i < amostras.length; i += MAX_METRICAS_POR_ENVIO) {
    const lote = {
      ...(temLogin && { LoginId: loginId }),
      origin: window.location.origin,
      metricas: amostras.slice(i, i + MAX_METRICAS_POR_ENVIO),
    };
    if (debug) {
      console.info("[web-vitals] lote", url, lote);
      continue;
    }
    // String vira text/plain: JSON cross-origin pediria preflight, que o sendBeacon não faz.
    const corpo = JSON.stringify(lote);
    if (navigator.sendBeacon?.(url, corpo)) continue;
    void fetch(url, { method: "POST", body: corpo, keepalive: true }).catch(() => undefined);
  }
}

let iniciado = false;

export function iniciarWebVitals(): void {
  if (iniciado || typeof window === "undefined") return;
  iniciado = true;

  const debug =
    import.meta.env.DEV && new URLSearchParams(window.location.search).has("webVitalsDebug");
  if (!debug) {
    if (!import.meta.env.PROD || isCurrentLocalDevHost()) return;
    // /app e /biblia têm pouco tráfego (~5 visitas/dia): com 10% ficavam dias sem nenhuma amostra.
    const publicaMedida = ehRotaPublicaMedida(normalizarRota(window.location.pathname));
    if (!publicaMedida && Math.random() >= taxaAmostragem(import.meta.env.VITE_WEB_VITALS_SAMPLE_RATE)) return;
  }

  const rotaInicial = normalizarRota(window.location.pathname);
  const fila = new Map<string, AmostraWebVital>();

  const enfileirar = (metrica: Metrica) => {
    // LCP e TTFB são da rota em que a página abriu; INP e CLS acumulam até a aba sair.
    const rota =
      metrica.name === "LCP" || metrica.name === "TTFB"
        ? rotaInicial
        : normalizarRota(window.location.pathname);
    // Demais páginas públicas já são medidas pelo PageSpeed/Unlighthouse.
    if (!debug && !store.getState().authReducer?.id && !ehRotaPublicaMedida(rota)) return;
    const elemento = elementoResponsavel(metrica);
    const modulo = moduloAtivo();
    fila.set(metrica.id, {
      id: metrica.id,
      nome: metrica.name,
      valor:
        metrica.name === "CLS"
          ? Math.round(metrica.value * 10000) / 10000
          : Math.round(metrica.value),
      rating: metrica.rating,
      rota,
      navegacao: metrica.navigationType,
      ...(elemento && { elemento }),
      ...(modulo && { modulo }),
      ...temposAtribuicao(metrica),
    });
  };

  const descarregar = () => {
    if (fila.size === 0) return;
    const amostras = [...fila.values()];
    fila.clear();
    enviar(amostras, debug);
  };

  // Fora do caminho crítico: a lib só baixa depois que o app já renderizou.
  void import("web-vitals/attribution")
    .then(({ onLCP, onINP, onCLS, onTTFB }) => {
      onTTFB(enfileirar);
      onLCP(enfileirar);
      onINP(enfileirar);
      onCLS(enfileirar);
      // Registrados depois dos on*: a lib reporta LCP/INP/CLS no mesmo evento e precisa vir antes.
      addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") descarregar();
      });
      // Safari nem sempre dispara visibilitychange ao descarregar a página.
      addEventListener("pagehide", descarregar);
    })
    .catch(() => undefined);
}
